import { useCallback, useEffect, useState } from "react"
import classNames from "classnames"
import styled from "styled-components"

import { useAppDispatch, useAppSelector } from "store/hooks"
import {
  contractorThemeSelector,
  mobileMenuOpenSelector,
  setVerticalMenuOpen,
  setVerticalMenuOpenMobile,
  userLogoSelector,
  verticalMenuOpenSelector,
} from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import styles from "assets/scss/index.module.scss"
import logo from "assets/svg/logo-white.svg"
import { ReactComponent as MenuArrowRight } from "assets/svg/menu-arrow-right.svg"

import { isRestrictedSubscriptionPlan } from "utils/isRestrictedSubscriptionPlan"
import { useWindowResize } from "hooks/useWindowResize"
import { Hamburger } from "components/common/Hamburger"
import { NotificationIcon } from "components/common/NotificationIcon"
import { UserMenu } from "components/common/UserMenu"
import { VerticalMenu } from "components/common/VerticalMenu"
import { ContractorMenu } from "components/common/VerticalMenu/menus"

import {
  HamburgerWrapper,
  LeftSideWrapper,
  LogoWrapper,
  MenuToggleButton,
  RightSideIconsWrapper,
  StyledLogo,
} from "../styled"
import { UniversityIcon } from "./UniversityIcon"

const StyledHeader = styled.header<{ backgroundStyle: string; isSticky: boolean }>`
  ${({ isSticky, backgroundStyle }) => isSticky && `background: ${backgroundStyle} !important;`}
`

export const ContractorTopbar = () => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [isSticky, setSticky] = useState(false)
  const desktopMenuOpen = useAppSelector(verticalMenuOpenSelector)
  const mobileMenuOpen = useAppSelector(mobileMenuOpenSelector)
  const isLargeScreen = useWindowResize()
  const menuOpen = isLargeScreen ? desktopMenuOpen : mobileMenuOpen
  const current_subscription = userProfile?.contractor?.subscription_plan
  const hasEPlusPlan = current_subscription === SubscriptionPlan.E_PLUS
  const universityAccessible = hasEPlusPlan || userProfile?.contractor?.university_accessible || false
  const isRestricted = isRestrictedSubscriptionPlan(userProfile)
  const shouldHideMenu = isRestricted && universityAccessible
  const currentTheme = useAppSelector(contractorThemeSelector)
  const userLogo = useAppSelector(userLogoSelector)

  const handleScroll = () => {
    const distance = window.scrollY

    if (distance > 0 && !isSticky) {
      setSticky(true)
    } else if (distance === 0 && isSticky) {
      setSticky(false)
    }
  }

  useEffect(() => {
    const setStickyOnScroll = () => handleScroll()

    window.addEventListener("scroll", setStickyOnScroll)

    return () => {
      window.removeEventListener("scroll", setStickyOnScroll)
    }
  }, [isSticky])

  const getBackgroundStyle = (hasEPlusPlan: boolean) => {
    if (hasEPlusPlan) {
      return currentTheme.backgroundGradient
    }
    return ContractorThemes.default.backgroundGradient
  }

  // Toggle menu state - use non-persisting action on mobile
  const handleMenuToggle = useCallback(() => {
    if (isLargeScreen) {
      dispatch(setVerticalMenuOpen(!menuOpen))
    } else {
      dispatch(setVerticalMenuOpenMobile(!menuOpen))
    }
  }, [dispatch, menuOpen, isLargeScreen])

  return (
    <StyledHeader
      backgroundStyle={getBackgroundStyle(hasEPlusPlan)}
      isSticky={isSticky}
      className={classNames(styles.topbar, { [styles.sticky]: isSticky })}
    >
      {!shouldHideMenu && (
        <VerticalMenu>
          <ContractorMenu />
        </VerticalMenu>
      )}
      <LeftSideWrapper>
        {!shouldHideMenu && (
          <>
            <MenuToggleButton
              type="button"
              $isOpen={menuOpen}
              onClick={handleMenuToggle}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <MenuArrowRight />
            </MenuToggleButton>
            <HamburgerWrapper>
              <Hamburger isOpen={menuOpen} onClick={handleMenuToggle} />
            </HamburgerWrapper>
          </>
        )}
        <LogoWrapper className={styles.logoWrapper} to="/">
          <StyledLogo className={styles.logo} src={userLogo || logo} alt="logo" />
        </LogoWrapper>
      </LeftSideWrapper>
      <RightSideIconsWrapper>
        <UniversityIcon />
        <NotificationIcon />
        <UserMenu />
      </RightSideIconsWrapper>
    </StyledHeader>
  )
}
